import { publicInstance } from '../../axiosInstance';

export interface Label {
    label: string;
    color: string;
}

export interface DeviceWithLabels {
    box: number;
    label: string[] | null;
}

// Fetch all labels
export const fetchLabels = async (): Promise<Label[]> => {
    const response = await publicInstance.get('/dacapi_inventory_label_colors');
    return response.data;
};

// Create a new label
export const createLabel = async (label: string, color: string): Promise<void> => {
    await publicInstance.post('/dacapi_inventory_label_colors', {
        label: label.trim(),
        color: color
    });
};

// Delete a label
export const deleteLabel = async (label: string): Promise<void> => {
    await publicInstance.delete(`/dacapi_inventory_label_colors/${encodeURIComponent(label)}`);
};

// Fetch current labels for selected devices
export const fetchDeviceLabels = async (devices: string[]): Promise<DeviceWithLabels[]> => {
    if (devices.length === 0) return [];
    
    try {
        const response = await publicInstance.get(`/view_dacapi_inventory_all?box=in.(${devices.join(',')})`);
        
        // Process response to ensure labels are strings
        const processedData = response.data.map((device: any) => {
            // If device.label is an array of objects with "label" property, extract just the label strings
            if (device.label && Array.isArray(device.label)) {
                const processedLabels = device.label.map((labelItem: any) => {
                    if (typeof labelItem === 'string') {
                        return labelItem;
                    } else if (typeof labelItem === 'object' && labelItem !== null && 'label' in labelItem) {
                        return String(labelItem.label);
                    } else {
                        return String(labelItem);
                    }
                }).filter(Boolean);
                
                return {
                    ...device,
                    label: processedLabels.length > 0 ? processedLabels : null
                };
            }
            return device;
        });
        
        return processedData;
    } catch (error) {
        console.error('Error fetching device labels:', error);
        throw error;
    }
};

// Assign labels to devices
export const assignLabels = async (
    devices: string[], 
    labels: string[], 
    serviceDevices: string[],
    replaceExisting: boolean = false
): Promise<void> => {
    // If we're replacing existing labels or removing all labels (empty array)
    if (replaceExisting || labels.length === 0) {
    const promises = devices.map(async (box) => {
        const isServiceDevice = serviceDevices.includes(box);
        const endpoint = isServiceDevice ? '/dacapi_inventory_temp' : '/dacapi_inventory_current';
        
        await publicInstance.post(endpoint, {
            box: parseInt(box),
            label: labels.length > 0 ? labels : null
        }, {
            headers: {
                'Prefer': 'resolution=merge-duplicates'
            }
        });
    });

    await Promise.all(promises);
    } 
    // If we're adding labels to existing ones
    else {
        // First get current labels for all devices
        const deviceLabelsData = await fetchDeviceLabels(devices);
        
        const promises = devices.map(async (boxStr) => {
            const box = parseInt(boxStr);
            const isServiceDevice = serviceDevices.includes(boxStr);
            const endpoint = isServiceDevice ? '/dacapi_inventory_temp' : '/dacapi_inventory_current';
            
            // Find current device's labels
            const deviceData = deviceLabelsData.find(d => d.box === box);
            const currentLabels = deviceData?.label || [];
            
            // Combine current labels with new ones, avoiding duplicates
            const combinedLabels = [...new Set([...currentLabels, ...labels])];
            
            await publicInstance.post(endpoint, {
                box: box,
                label: combinedLabels
            }, {
                headers: {
                    'Prefer': 'resolution=merge-duplicates'
                }
            });
        });

        await Promise.all(promises);
    }
}; 